// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Hand-rolled SVG chart primitives. No chart library — see factory rules. */
import { escapeHtml, formatCompact, formatNumber } from '../utils/format';

export const SVG_NS = 'http://www.w3.org/2000/svg';

export function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

export function niceCeil(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(max));
  const norm = max / mag;
  let step: number;
  if (norm <= 1) step = 1;
  else if (norm <= 2) step = 2;
  else if (norm <= 2.5) step = 2.5;
  else if (norm <= 5) step = 5;
  else step = 10;
  return step * mag;
}

export function ticks(max: number, count = 5): number[] {
  const top = niceCeil(max);
  return Array.from({ length: count + 1 }, (_, i) => (top / count) * i);
}

// ── Horizontal ranked bars ────────────────────────────────────────────────────
export interface HBarDatum {
  id: string;
  label: string;
  value: number;
  colour: string;
  tip: string;
  suffix?: string;
}
export function horizontalBars(
  data: HBarDatum[],
  opts: { width?: number; rowHeight?: number; labelWidth?: number; valueFormat?: (v: number) => string; onClick?: (id: string) => void } = {},
): SVGSVGElement {
  const width = opts.width ?? 900;
  const rowH = opts.rowHeight ?? 28;
  const labelW = opts.labelWidth ?? 150;
  const fmt = opts.valueFormat ?? ((v: number) => formatNumber(v));
  const valueW = 74;
  const barMax = Math.max(60, width - labelW - valueW - 16);
  const height = Math.max(1, data.length) * rowH + 8;
  const max = niceCeil(Math.max(...data.map((d) => d.value), 0.0001));
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  data.forEach((d, i) => {
    const y = i * rowH + 4;
    const g = el('g');
    const label = el('text', { x: labelW - 8, y: y + rowH / 2, 'text-anchor': 'end', 'dominant-baseline': 'middle', 'font-size': 12 });
    label.textContent = d.label;
    g.appendChild(label);
    g.appendChild(el('rect', { x: labelW, y: y + 3, width: barMax, height: rowH - 9, rx: 3, fill: 'var(--bg-elevated)' }));
    const w = Math.max(1, (d.value / max) * barMax);
    const bar = el('rect', { class: 'bar', x: labelW, y: y + 3, width: w, height: rowH - 9, rx: 3, fill: d.colour, 'data-tip': d.tip, 'aria-label': d.tip, tabindex: 0, role: 'button' });
    g.appendChild(bar);
    const val = el('text', { class: 'val-label', x: labelW + barMax + 8, y: y + rowH / 2, 'dominant-baseline': 'middle' });
    val.textContent = fmt(d.value) + (d.suffix ? ` ${d.suffix}` : '');
    g.appendChild(val);
    const hit = el('rect', { x: 0, y, width, height: rowH, fill: 'transparent', style: 'cursor:pointer', 'data-tip': d.tip });
    g.appendChild(hit);
    if (opts.onClick) {
      const fire = () => opts.onClick!(d.id);
      g.addEventListener('click', fire);
      bar.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
          e.preventDefault();
          fire();
        }
      });
    }
    svg.appendChild(g);
  });
  return svg;
}

// ── Stacked columns (e.g. incidents per decade, coloured by outcome) ──────────
export interface StackSeg {
  key: string;
  colour: string;
  label: string;
}
export interface StackDatum {
  id: string;
  label: string;
  values: Record<string, number>;
  extraTip?: string;
}
export function stackedColumns(
  data: StackDatum[],
  segs: StackSeg[],
  opts: { width?: number; height?: number; labelEvery?: number; onClick?: (id: string) => void; annotations?: Array<{ id: string; text: string }> } = {},
): SVGSVGElement {
  const width = opts.width ?? 960;
  const height = opts.height ?? 340;
  const m = { top: 18, right: 16, bottom: 40, left: 48 };
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;
  const totals = data.map((d) => segs.reduce((s, sg) => s + (d.values[sg.key] || 0), 0));
  const max = niceCeil(Math.max(...totals, 1));
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  for (const t of ticks(max, 4)) {
    const y = m.top + plotH - (t / max) * plotH;
    svg.appendChild(el('line', { class: 'grid-line', x1: m.left, y1: y, x2: m.left + plotW, y2: y }));
    const lbl = el('text', { class: 'axis-label', x: m.left - 6, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    lbl.textContent = formatCompact(t);
    svg.appendChild(lbl);
  }
  const slot = plotW / Math.max(1, data.length);
  const barW = Math.max(1, slot * 0.74);
  const labelEvery = opts.labelEvery ?? Math.max(1, Math.ceil(data.length / 16));
  data.forEach((d, i) => {
    const x = m.left + i * slot + (slot - barW) / 2;
    let cy = m.top + plotH;
    const total = totals[i];
    for (const sg of segs) {
      const v = d.values[sg.key] || 0;
      if (v <= 0) continue;
      const h = (v / max) * plotH;
      cy -= h;
      svg.appendChild(
        el('rect', {
          x,
          y: cy,
          width: barW,
          height: h,
          fill: sg.colour,
          'data-tip': `${d.label} — ${sg.label}: ${formatNumber(v)} of ${formatNumber(total)}${d.extraTip ? `\n${d.extraTip}` : ''}`,
        }),
      );
    }
    // full-column hit target
    const hit = el('rect', { x: m.left + i * slot, y: m.top, width: slot, height: plotH, fill: 'transparent', 'data-tip': `${d.label}: ${formatNumber(total)} incidents${d.extraTip ? `\n${d.extraTip}` : ''}`, style: opts.onClick ? 'cursor:pointer' : '' });
    if (opts.onClick) hit.addEventListener('click', () => opts.onClick!(d.id));
    svg.appendChild(hit);
    if (i % labelEvery === 0) {
      const lbl = el('text', { class: 'axis-label', x: x + barW / 2, y: height - m.bottom + 14, 'text-anchor': 'middle' });
      lbl.textContent = d.label;
      svg.appendChild(lbl);
    }
  });
  for (const a of opts.annotations ?? []) {
    const idx = data.findIndex((d) => d.id === a.id);
    if (idx < 0) continue;
    const x = m.left + idx * slot + slot / 2;
    svg.appendChild(el('line', { x1: x, y1: m.top, x2: x, y2: m.top + plotH, stroke: '#0e2233', 'stroke-width': 1, 'stroke-dasharray': '3 3', opacity: 0.5 }));
    svg.appendChild(el('circle', { cx: x, cy: m.top + 3, r: 4, fill: '#0e2233', 'data-tip': a.text, style: 'cursor:help' }));
  }
  svg.appendChild(el('line', { class: 'axis-line', x1: m.left, y1: m.top + plotH, x2: m.left + plotW, y2: m.top + plotH }));
  return svg;
}

// ── Multi-line chart ──────────────────────────────────────────────────────────
export interface LineSeries {
  id: string;
  label: string;
  colour: string;
  values: Array<number | null>;
}
export function multiLine(
  xLabels: Array<string | number>,
  series: LineSeries[],
  opts: { width?: number; height?: number; yMax?: number; yFmt?: (v: number) => string; showEndLabel?: boolean; onPoint?: (sid: string) => void } = {},
): SVGSVGElement {
  const width = opts.width ?? 960;
  const height = opts.height ?? 340;
  const m = { top: 16, right: opts.showEndLabel === false ? 20 : 92, bottom: 34, left: 46 };
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;
  const n = xLabels.length;
  const allVals = series.flatMap((s) => s.values.filter((v): v is number => v != null));
  const yMax = opts.yMax ?? niceCeil(Math.max(...allVals, 0.0001));
  const yFmt = opts.yFmt ?? ((v: number) => formatCompact(v));
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  const xAt = (i: number) => m.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yAt = (v: number) => m.top + plotH - (v / yMax) * plotH;

  for (const t of ticks(yMax, 4)) {
    const y = yAt(t);
    svg.appendChild(el('line', { class: 'grid-line', x1: m.left, y1: y, x2: m.left + plotW, y2: y }));
    const lbl = el('text', { class: 'axis-label', x: m.left - 6, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    lbl.textContent = yFmt(t);
    svg.appendChild(lbl);
  }
  xLabels.forEach((xl, i) => {
    if (i % Math.max(1, Math.ceil(n / 12)) === 0 || i === n - 1) {
      const lbl = el('text', { class: 'axis-label', x: xAt(i), y: height - m.bottom + 14, 'text-anchor': 'middle' });
      lbl.textContent = String(xl);
      svg.appendChild(lbl);
    }
  });
  svg.appendChild(el('line', { class: 'axis-line', x1: m.left, y1: m.top + plotH, x2: m.left + plotW, y2: m.top + plotH }));

  for (const s of series) {
    const pts = s.values.map((v, i) => (v == null ? null : `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`)).filter((p): p is string => p != null);
    if (!pts.length) continue;
    svg.appendChild(el('polyline', { points: pts.join(' '), fill: 'none', stroke: s.colour, 'stroke-width': 2.4, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }));
    s.values.forEach((v, i) => {
      if (v == null) return;
      const dot = el('circle', { cx: xAt(i), cy: yAt(v), r: 2.8, fill: s.colour, 'data-tip': `${s.label} · ${xLabels[i]}: ${yFmt(v)}`, style: opts.onPoint ? 'cursor:pointer' : '' });
      if (opts.onPoint) dot.addEventListener('click', () => opts.onPoint!(s.id));
      svg.appendChild(dot);
    });
    if (opts.showEndLabel !== false) {
      const lastIdx = s.values.map((v, i) => (v != null ? i : -1)).filter((i) => i >= 0).pop();
      if (lastIdx != null) {
        const lbl = el('text', { x: xAt(lastIdx) + 8, y: yAt(s.values[lastIdx] as number), 'dominant-baseline': 'middle', 'font-size': 11, 'font-weight': 600, fill: s.colour });
        lbl.textContent = s.label;
        svg.appendChild(lbl);
      }
    }
  }
  return svg;
}

// ── Donut ──────────────────────────────────────────────────────────────────────
export interface DonutSeg {
  value: number;
  colour: string;
  label: string;
}
export function donut(segments: DonutSeg[], opts: { size?: number; centreLabel?: string; centreSub?: string } = {}): SVGSVGElement {
  const size = opts.size ?? 200;
  const r = size / 2;
  const inner = r * 0.6;
  const cx = r;
  const cy = r;
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${size} ${size}`, width: size, height: size, role: 'img' });
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let a0 = -Math.PI / 2;
  for (const seg of segments) {
    const frac = seg.value / total;
    if (frac <= 0) continue;
    const a1 = a0 + frac * Math.PI * 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const xi0 = cx + inner * Math.cos(a1), yi0 = cy + inner * Math.sin(a1);
    const xi1 = cx + inner * Math.cos(a0), yi1 = cy + inner * Math.sin(a0);
    const d = `M${x0.toFixed(2)},${y0.toFixed(2)} A${r},${r} 0 ${large} 1 ${x1.toFixed(2)},${y1.toFixed(2)} L${xi0.toFixed(2)},${yi0.toFixed(2)} A${inner},${inner} 0 ${large} 0 ${xi1.toFixed(2)},${yi1.toFixed(2)} Z`;
    svg.appendChild(el('path', { d, fill: seg.colour, stroke: 'var(--bg-panel)', 'stroke-width': 1.5, 'data-tip': `${seg.label}: ${formatNumber(seg.value)} (${(frac * 100).toFixed(1)}%)`, style: 'cursor:default' }));
    a0 = a1;
  }
  if (opts.centreLabel) {
    const t = el('text', { x: cx, y: cy - 2, 'text-anchor': 'middle', 'font-size': size * 0.19, 'font-weight': 800, fill: 'var(--text-primary)', 'font-family': 'var(--font-mono)' });
    t.textContent = opts.centreLabel;
    svg.appendChild(t);
    if (opts.centreSub) {
      const s = el('text', { x: cx, y: cy + size * 0.12, 'text-anchor': 'middle', 'font-size': size * 0.075, fill: 'var(--text-tertiary)' });
      s.textContent = opts.centreSub;
      svg.appendChild(s);
    }
  }
  return svg;
}

// ── 100%-stacked outcome bar (HTML) ────────────────────────────────────────────
export function stackedBarHtml(segs: DonutSeg[], opts: { tall?: boolean } = {}): string {
  const total = segs.reduce((s, x) => s + x.value, 0);
  if (total <= 0) return '<div class="mix-bar"></div>';
  const parts = segs
    .filter((s) => s.value > 0)
    .map((s) => {
      const pct = (s.value / total) * 100;
      const tip = escapeHtml(`${s.label}: ${formatNumber(s.value)} (${pct.toFixed(1)}%)`);
      return `<div class="mix-seg" style="flex:${s.value};background:${s.colour}" data-tip="${tip}" aria-label="${tip}"></div>`;
    })
    .join('');
  return `<div class="mix-bar${opts.tall ? ' tall' : ''}">${parts}</div>`;
}

// ── Sequential colour ramp (ocean teal) for the choropleth ─────────────────────
export const RAMP = ['#dceff1', '#b6dfe3', '#83c6cd', '#4fa9b3', '#2b8994', '#146b76', '#0a4a52'];
export function rampColour(value: number | null, stops: number[]): string {
  if (value == null || !Number.isFinite(value)) return '#e6ebf0';
  for (let i = 0; i < stops.length; i++) if (value < stops[i]) return RAMP[i];
  return RAMP[RAMP.length - 1];
}

/** Heat colour for a matrix cell, t in [0,1] on the ocean ramp. */
export function heatColour(t: number): string {
  const c = Math.max(0, Math.min(1, t));
  const idx = Math.min(RAMP.length - 1, Math.floor(c * (RAMP.length - 1) + 0.001));
  return RAMP[idx];
}

export { escapeHtml };
